import { defineStore } from "pinia";
import dayjs from "dayjs";
import _ from "lodash";
import receipt_one from "~/samples/receipts/receipt_one.json";
import ThermalPrintReceipt from "~/class/printer/ThermalPrintReceipt";
import type { ReceiptOrderDynamodb } from "~/interface/ReceiptOrder";
import { bucket, isOfflineBoot, offlineBucketNames } from "~/services/storage/offlineBuckets";
import { loadPerRowBucket, mirrorRowToIDB } from "~/services/bootstrap/layerBHelpers";
import { syncPendingActivities } from "~/plugins/job.to.activity.plugin";
import { usePosEmployeesStore } from "./pos.employees";



const LOCAL_STORAGE_PREFIX = "receipt-dynamodb";

// Offline void queue. When a void can't reach the server (offline / server
// down), the intent is persisted here and replayed once connectivity returns
// — AFTER all pending receipts have been synced (so the server sort key
// exists to address the DELETE).
const VOID_COMMAND_PREFIX = "void-command";

type VoidCommand = {
  nonce: string; // receipt client id — stable across sync, primary key
  sortKey?: string; // datetime-table-grandtotal if already known at enqueue
  voidReason: string;
  voidedBy?: string; // PIN'd employee at void time — shown in the void email
  approvedBy?: string; // manager whose PIN approved the void, if one was needed
  createdAt: number;
};

// IDB mirror bucket for the current customer's receipts. One entry per
// receipt, keyed by Nonce (or fallback to datetime-table-grandtotal / id).
function getReceiptsBucket() {
  const sid = useAuthStore().storeId;
  if (!sid) return null;
  return bucket(offlineBucketNames.receipts(String(sid)));
}

function receiptKey(r: ReceiptOrderDynamodb): string {
  return (
    (r as any)?.Nonce ||
    r?.["datetime-table-grandtotal"] ||
    (r as any)?.receiptId ||
    ""
  );
}

export const usePosReceiptsStore = defineStore("receipts", () => {
  const dataservice = useDataservice();
  // Receipt running-number counter lives in its own store — see
  // pos.receiptIdCounter.ts for the claim/flush/reconcile machinery.
  const receiptIdCounter = usePosReceiptIdCounterStore();
  const receipts = ref<ReceiptOrderDynamodb[]>([]);

  const queryReceipts = ref({
    startDate: dayjs().format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  })


  const subLoaded = ref({
    receipts: false,
  });

  const loaded = computed(() => subLoaded.value.receipts && receiptIdCounter.loaded);
  const data = computed(() => receipts.value);

  const isInitialized = ref(false);

  // Pending receipts = receipts saved to localStorage but not yet posted to cloud.
  // Source of truth for "unsynced". Reset to [] after successful post.
  const pendingReceipts = ref<ReceiptOrderDynamodb[]>([]);

  // In-memory dedup set of receipt keys currently mirrored in IDB. Used to
  // avoid redundant IDB writes when cloud fetch returns receipts we already
  // have (other terminal scenarios, repeated fetches).
  const knownKeys = new Set<string>();

  const mirrorToIDB = async (r: ReceiptOrderDynamodb) => {
    const key = receiptKey(r);
    mirrorRowToIDB(getReceiptsBucket(), knownKeys, key, r);
  };

  const mirrorManyToIDB = async (list: ReceiptOrderDynamodb[]) => {
    for (const r of list) {
      await mirrorToIDB(r);
    }
  };

  const refreshPendingReceipts = () => {
    pendingReceipts.value = getPendingReceipts();
  };

  const pendingNonces = computed(
    () => new Set(pendingReceipts.value.map((r) => r.Nonce).filter(Boolean) as string[])
  );

  // ── Offline void queue ────────────────────────────────────────────
  const voidCommands = ref<VoidCommand[]>([]);

  const getVoidCommands = (): VoidCommand[] => {
    if (!process.client) return [];
    try {
      const out: VoidCommand[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`${VOID_COMMAND_PREFIX}:`)) {
          const stored = localStorage.getItem(key);
          if (stored) out.push(JSON.parse(stored) as VoidCommand);
        }
      }
      return out;
    } catch (error) {
      console.error("Failed to read void commands from local storage:", error);
      return [];
    }
  };

  const refreshVoidCommands = () => {
    voidCommands.value = getVoidCommands();
  };

  // Name of whoever is voiding, for the server's void-notification email.
  // The getter falls back to "unknown" — send "" so the server uses receipt.User.
  const currentVoidedBy = (): string => {
    const name = usePosEmployeesStore().getCurrentEmployee?.name || "";
    return name === "unknown" ? "" : name;
  };

  const enqueueVoidCommand = (
    receiptOrder: ReceiptOrderDynamodb,
    voidReason: string,
    approvedBy?: string
  ) => {
    if (!process.client) return;
    const nonce = receiptOrder?.Nonce;
    if (!nonce) return;
    try {
      const cmd: VoidCommand = {
        nonce,
        sortKey: findReceiptSortKey(receiptOrder),
        voidReason,
        voidedBy: currentVoidedBy(),
        approvedBy: approvedBy || undefined,
        createdAt: Date.now(),
      };
      localStorage.setItem(`${VOID_COMMAND_PREFIX}:${nonce}`, JSON.stringify(cmd));
      refreshVoidCommands();
    } catch (error) {
      console.error("Failed to enqueue void command:", error);
    }
  };

  const dequeueVoidCommand = (nonce?: string) => {
    if (!process.client || !nonce) return;
    try {
      localStorage.removeItem(`${VOID_COMMAND_PREFIX}:${nonce}`);
      refreshVoidCommands();
    } catch (error) {
      console.error("Failed to dequeue void command:", error);
    }
  };

  const pendingVoidNonces = computed(
    () => new Set(voidCommands.value.map((c) => c.nonce).filter(Boolean))
  );

  // True when a receipt has a void queued but not yet synced to the server.
  // Components use this to render the strikethrough + "pending sync" badge.
  const isVoidPending = (receiptOrder: ReceiptOrderDynamodb): boolean => {
    const nonce = receiptOrder?.Nonce;
    return !!nonce && pendingVoidNonces.value.has(nonce);
  };

  const unsyncedCount = computed(
    () => pendingReceipts.value.length + voidCommands.value.length
  );

  // Delayed (debounced) unsynced count — only reflects "still unsynced after 5s",
  // so brief in-flight saves (~normal sync time) don't trigger the UI badge
  const delayedUnsyncedCount = ref(0);
  let unsyncedTimer: any = null;
  watch(unsyncedCount, (n) => {
    if (unsyncedTimer) clearTimeout(unsyncedTimer);
    if (n === 0) {
      delayedUnsyncedCount.value = 0;
      return;
    }
    unsyncedTimer = setTimeout(() => {
      delayedUnsyncedCount.value = unsyncedCount.value;
    }, 5000);
  });

  const isReceiptUnsynced = (nonce?: string) => !!nonce && pendingNonces.value.has(nonce);

  const saveLocalStorage = (receiptOrder: ReceiptOrderDynamodb) => {
    if (!process.client) return;

    try {
      const key = `${LOCAL_STORAGE_PREFIX}:${receiptOrder.Nonce || Date.now()}`;
      localStorage.setItem(
        key,
        JSON.stringify({
          ...receiptOrder,
          Synced: false,
        })
      );
      refreshPendingReceipts();
    } catch (error) {
      console.error("Failed to save receipt to local storage:", error);
    }
  };

  const removeLocalStorage = (receiptOrder: ReceiptOrderDynamodb) => {
    if (!process.client) return;

    try {
      const key = `${LOCAL_STORAGE_PREFIX}:${receiptOrder.Nonce}`;
      localStorage.removeItem(key);
      refreshPendingReceipts();
    } catch (error) {
      console.error("Failed to remove receipt from local storage:", error);
    }
  };

  const markReceiptSynced = (nonce?: string) => {
    if (!nonce) return;

    const index = receipts.value.findIndex((r) => r.Nonce === nonce);
    if (index !== -1 && receipts.value[index]) {
      receipts.value[index].Synced = true;
    }
  };

  const getPendingReceipts = (): ReceiptOrderDynamodb[] => {
    if (!process.client) return [];

    try {
      const pending: ReceiptOrderDynamodb[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(LOCAL_STORAGE_PREFIX)) {
          const stored = localStorage.getItem(key);
          if (stored) {
            pending.push(JSON.parse(stored) as ReceiptOrderDynamodb);
          }
        }
      }
      return pending;
    } catch (error) {
      console.error("Failed to get pending receipts from local storage:", error);
      return [];
    }
  };

  const dedupeKey = (r: ReceiptOrderDynamodb): string => {
    return (
      (r as any)?.Nonce ||
      (r as any)?.hash ||
      (r as any)?.Hash ||
      r?.["datetime-table-grandtotal"] ||
      (r as any)?.receiptId ||
      ""
    );
  };

  const dedupeReceipts = (list: ReceiptOrderDynamodb[]): ReceiptOrderDynamodb[] => {
    const map = new Map<string, ReceiptOrderDynamodb>();
    for (const r of list) {
      const key = dedupeKey(r);
      if (!key) {
        map.set(`__noKey:${map.size}`, r);
        continue;
      }
      const existing = map.get(key);
      if (!existing) {
        map.set(key, r);
      } else {
        const existingSynced = (existing as any).Synced !== false;
        const incomingSynced = (r as any).Synced !== false;
        // Prefer synced (server) version over local pending
        if (!existingSynced && incomingSynced) map.set(key, r);
      }
    }
    return Array.from(map.values());
  };

  const mergePendingReceiptsIntoState = (pendingReceipts: ReceiptOrderDynamodb[] = getPendingReceipts()) => {
    const pendingMarked = pendingReceipts.map((r) => ({ ...r, Synced: false }));
    const combined = [...receipts.value, ...pendingMarked];
    const deduped = dedupeReceipts(combined);

    // Overlay: flag receipts with a queued (not-yet-synced) void so the UI can
    // render them struck-through with a "pending sync" badge.
    const voided = pendingVoidNonces.value;
    const overlaid = deduped.map((r) =>
      r?.Nonce && voided.has(r.Nonce)
        ? ({ ...r, _voidPending: true } as ReceiptOrderDynamodb)
        : r
    );

    receipts.value = _.orderBy(
      overlaid,
      ["Date", "OrderDate", "createdAt"],
      ["desc", "desc", "desc"]
    );

    return receipts.value;
  };

  // `Synced` is a local-only flag: it tells the offline queue whether a receipt
  // has reached the server yet. Stripped on the way out so it never becomes a
  // stored column, but kept on the local mirror, where mergePendingReceipts
  // relies on it to prefer the server copy over a pending one.
  const toApiPayload = (receiptOrder: ReceiptOrderDynamodb): ReceiptOrderDynamodb => {
    const { Synced, ...rest } = receiptOrder as ReceiptOrderDynamodb & { Synced?: boolean };
    return rest as ReceiptOrderDynamodb;
  };

  const post = async (receiptOrder: ReceiptOrderDynamodb) => {
    try {
      const response = await dataservice.receipts.post(toApiPayload(receiptOrder));
      markReceiptSynced(receiptOrder.Nonce);
      return response;
    } catch (error) {
      console.error("Failed to post receipt to API:", error);
      throw error;
    }
  };

  const syncPendingReceipts = async () => {
    const pending = getPendingReceipts();

    for (const item of pending) {
      try {
        await post(item);
        removeLocalStorage(item);
        markReceiptSynced(item.Nonce);
      } catch (error) {
        console.error("Failed to sync pending receipt:", item.receiptId, error);
      }
    }

    mergePendingReceiptsIntoState();
  };

  const fetch = async (
    startDate: string = dayjs().format("YYYY-MM-DD"),
    endDate: string = dayjs().format("YYYY-MM-DD"),
  ): Promise<ReceiptOrderDynamodb[]> => {
    try {
      await dataservice.receipts.fetchBetween(startDate, endDate);
      const result = (dataservice.receipts.data.value || []) as ReceiptOrderDynamodb[];
      console.log('[receipts fetch] result count:', result.length, '| startDate:', startDate, 'endDate:', endDate);
      receipts.value = result;
      mergePendingReceiptsIntoState();
      // Mirror cloud results to IDB. mirrorToIDB dedupes via knownKeys, so
      // overlapping fetches (another terminal paid in parallel) don't write
      // the same receipt twice.
      void mirrorManyToIDB(result);
      subLoaded.value.receipts = true;
      return result;
    } catch (error) {
      console.error("[receipts fetch] FAILED:", error, '| startDate:', startDate, 'endDate:', endDate);
      mergePendingReceiptsIntoState();
      subLoaded.value.receipts = true;
      return [];
    }
  };

  // Hard refresh from the server for the given range. Unlike fetch(), this
  // THROWS on failure instead of swallowing it — callers (e.g. close-shop) need
  // to tell a real server pull from a network error: an empty result on a
  // reachable server is a legit "no sales" day, whereas a throw means the data
  // is stale/local-only. On success it replaces the in-memory receipts with the
  // server set (which includes OTHER cashiers / 2nd cashiers) and re-merges this
  // terminal's local pending so nothing this device made is lost.
  const refreshFromServer = async (
    startDate: string = dayjs().format("YYYY-MM-DD"),
    endDate: string = dayjs().format("YYYY-MM-DD"),
  ): Promise<ReceiptOrderDynamodb[]> => {
    await dataservice.receipts.fetchBetween(startDate, endDate);
    const result = (dataservice.receipts.data.value || []) as ReceiptOrderDynamodb[];
    console.log('[receipts refreshFromServer] count:', result.length, '| range:', startDate, '→', endDate);
    receipts.value = result;
    mergePendingReceiptsIntoState();
    void mirrorManyToIDB(result);
    subLoaded.value.receipts = true;
    return result;
  };

  const add = async (receiptOrder: ReceiptOrderDynamodb) => {


    receipts.value.unshift(receiptOrder);
    saveLocalStorage(receiptOrder);
    // Mirror to IDB immediately (offline-first). knownKeys is updated so
    // the cloud fetch below won't re-write this same nonce.
    void mirrorToIDB(receiptOrder);
    mergePendingReceiptsIntoState([receiptOrder]);

    try {
      await post(receiptOrder);
      removeLocalStorage(receiptOrder);
      mergePendingReceiptsIntoState();
    } catch (error) {
      console.error("Failed to post receipt to API, kept in local storage:", error);
    }
  };

  const update = async (
    receiptOrder: ReceiptOrderDynamodb,
    updates: Partial<ReceiptOrderDynamodb>
  ) => {
    try {
      await dataservice.receipts.put(receiptOrder["datetime-table-grandtotal"], updates);
      await fetch();
    } catch (error) {
      console.error("Failed to update receipt in API:", error);
      throw error;
    }
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  // Time the backend needs to finish stock-deduction after a receipt POST
  // before a subsequent DELETE can correctly return stock.
  const STOCK_DEDUCTION_DELAY_MS = 3500;

  // After all pending receipts are POSTed, wait this long before replaying
  // queued voids so the server has finished writing them to the DB (and
  // assigning sort keys we then resolve via fetch).
  const VOID_REPLAY_DELAY_MS = 2000;

  const isOnline = () =>
    typeof navigator === "undefined" || navigator.onLine !== false;

  const httpStatus = (err: any): number | undefined =>
    err?.status || err?.statusCode || err?.response?.status;

  // Network failure (no status) or 5xx → server unreachable: safe to queue.
  const isNetworkOrServerError = (err: any): boolean => {
    const status = httpStatus(err);
    return status == null || status >= 500;
  };

  // Server says the receipt is already gone → treat replay as success.
  const isAlreadyVoided = (err: any): boolean => httpStatus(err) === 404;

  const findReceiptSortKey = (receiptOrder: ReceiptOrderDynamodb): string | undefined => {
    const direct = receiptOrder?.["datetime-table-grandtotal"];
    if (direct) return direct;

    const nonce = receiptOrder?.Nonce;
    if (nonce) {
      const inState = receipts.value.find((r) => r?.Nonce === nonce);
      if (inState?.["datetime-table-grandtotal"]) return inState["datetime-table-grandtotal"];
    }
    return undefined;
  };

  // Dedup in-flight background syncs so View + Void on the same row don't post twice.
  const _pendingSyncs = new Map<string, Promise<void>>();

  // Global serial chain: only ONE ensureSynced runs at a time. If the network
  // is slow and the user clicks several rows in a row, the second/third sync
  // wait for the previous to finish instead of firing parallel POSTs.
  let _syncSerial: Promise<void> = Promise.resolve();

  // Background "make sure this bill is uploaded to cloud" — POST only, never DELETE.
  // - Idempotent: returns the same in-flight promise per Nonce.
  // - Serialized: queues behind any other ensureSynced currently running.
  // - Re-checks state after the queue wait, so if a prior fetch already pulled
  //   the server-assigned sort key into state, this call becomes a no-op.
  const ensureSynced = (receiptOrder: ReceiptOrderDynamodb): Promise<void> => {
    if (findReceiptSortKey(receiptOrder)) return Promise.resolve();
    const nonce = receiptOrder?.Nonce;
    if (!nonce) return Promise.resolve();

    const existing = _pendingSyncs.get(nonce);
    if (existing) return existing;

    const job = _syncSerial.then(async () => {
      // Another sync may have already pulled this receipt's sort key into state
      // while we were queued — skip redundant POST.
      if (findReceiptSortKey(receiptOrder)) return;

      try {
        const response: any = await post(receiptOrder);
        const fromResponse = response?.["datetime-table-grandtotal"];
        if (fromResponse) {
          const idx = receipts.value.findIndex((r) => r?.Nonce === nonce);
          if (idx !== -1 && receipts.value[idx]) {
            receipts.value[idx] = { ...receipts.value[idx], ...response, Synced: true };
          }
          removeLocalStorage(receiptOrder);
        }
        // Let backend finish stock deduction, then refetch to pull the
        // server-assigned datetime-table-grandtotal into state.
        await sleep(STOCK_DEDUCTION_DELAY_MS);
        await fetch();
      } catch (error) {
        console.error("ensureSynced failed", error);
      }
    }).finally(() => {
      _pendingSyncs.delete(nonce);
    });

    _pendingSyncs.set(nonce, job);
    // Keep the chain alive even if a job throws.
    _syncSerial = job.catch(() => {});
    return job;
  };

  // Queue a void for later replay + optimistically mark the receipt voided.
  const queueVoid = (receiptOrder: ReceiptOrderDynamodb, reason: string, approvedBy?: string) => {
    enqueueVoidCommand(receiptOrder, reason, approvedBy);
    mergePendingReceiptsIntoState();
  };

  const remove = async (receiptOrder: ReceiptOrderDynamodb, voidReason?: string, approvedBy?: string) => {
    const reason = voidReason || "voided";

    // Offline → queue immediately (optimistic strikethrough). Replayed by
    // flushOfflineMutations once connectivity returns.
    if (!isOnline()) {
      queueVoid(receiptOrder, reason, approvedBy);
      return;
    }

    try {
      let sortKey = findReceiptSortKey(receiptOrder);

      // Unsynced receipt → sync it first so the server assigns a sort key.
      // ensureSynced does post + stock-delay + fetch.
      if (!sortKey) {
        await ensureSynced(receiptOrder);
        sortKey = findReceiptSortKey(receiptOrder);

        if (!sortKey) {
          await sleep(1500);
          await fetch();
          sortKey = findReceiptSortKey(receiptOrder);
        }
      }

      // Still no key → the receipt itself couldn't reach the server
      // (server down). Queue the void instead of failing.
      if (!sortKey) {
        queueVoid(receiptOrder, reason, approvedBy);
        return;
      }

      await dataservice.receipts.remove(sortKey, { voidReason: reason, voidedBy: currentVoidedBy(), approvedBy: approvedBy || "" });
      // Let the backend's void flow (return stock, write void event) settle,
      // then refetch so the UI reflects the server's authoritative state.
      await fetch();
    } catch (error) {
      if (isAlreadyVoided(error)) {
        // Server already removed it (e.g. another terminal / earlier replay).
        await fetch().catch(() => {});
        return;
      }
      if (isNetworkOrServerError(error)) {
        console.warn("[receipts.remove] server unreachable — queuing void", error);
        queueVoid(receiptOrder, reason, approvedBy);
        return;
      }
      console.error("Failed to delete receipt from API:", error);
      throw error;
    }
  };

  // Series replay: sync all pending receipts FIRST (so every bill gets its
  // server sort key), wait for the DB to settle, then replay queued voids.
  // Chained onto _syncSerial so it never races with post/ensureSynced.
  const flushOfflineMutations = (): Promise<void> => {
    const job = _syncSerial.then(async () => {
      // STEP 1 — push all pending receipts to the cloud.
      await syncPendingReceipts();

      if (voidCommands.value.length === 0) return;

      // STEP 2 — give the backend time to persist them.
      await sleep(VOID_REPLAY_DELAY_MS);

      // STEP 3 — fetch so server-assigned sort keys land in state and
      // findReceiptSortKey can resolve queued voids by Nonce.
      const bd = todaysBusinessDate();
      await fetch(bd, bd);

      // STEP 4 — replay each void serially.
      for (const cmd of [...voidCommands.value]) {
        const sortKey = cmd.sortKey || findReceiptSortKey({ Nonce: cmd.nonce } as any);
        if (!sortKey) continue; // can't resolve yet → keep queued for next flush
        try {
          await dataservice.receipts.remove(sortKey, { voidReason: cmd.voidReason, voidedBy: cmd.voidedBy, approvedBy: cmd.approvedBy });
          dequeueVoidCommand(cmd.nonce);
        } catch (e) {
          if (isAlreadyVoided(e)) dequeueVoidCommand(cmd.nonce);
          // other errors → keep queued, retry next flush
        }
      }

      // STEP 5 — reflect final server state.
      await fetch(bd, bd);
    });
    _syncSerial = job.catch(() => {});
    return job.catch((e) => console.error("[flushOfflineMutations] failed", e));
  };

  const refresh = () => {
    receipts.value = [];
    subLoaded.value.receipts = false;
  };

  const setReceipts = (values: any[]) => {
    receipts.value = _.orderBy(values || [], ["Date"], ["desc"]);
    subLoaded.value.receipts = true;
  };



  let syncInterval: any = null;

  function todaysBusinessDate(): string {
    const businessMode = useSettingsStore().getSystemEnvByName("BusinessMode")?.Value || "0";
    const now = dayjs();
    return businessMode == 1
      ? now.add(-7, "hour").format("YYYY-MM-DD")
      : now.format("YYYY-MM-DD");
  }

  function startAutoSyncInterval() {
    if (!process.client || syncInterval) return;
    syncInterval = setInterval(() => {
      receiptIdCounter.flushCounterIncrements().catch((e) => console.error("[counter auto-sync] failed:", e));
      flushOfflineMutations().catch((e) => console.error("[receipts auto-sync] failed:", e));
    }, 10 * 60 * 1000);
  }

  let reconnectWatchStop: (() => void) | null = null;

  // Everything that should be pushed to the cloud the moment connectivity
  // returns: queued receipts + voids, pending jobs, and pending activities.
  // Each guards/dedupes internally, so firing them together is safe.
  async function syncAllOnReconnect() {
    await Promise.allSettled([
      receiptIdCounter.flushCounterIncrements(),
      flushOfflineMutations(),
      usePosJobsStore().syncPendingJobs(),
      syncPendingActivities(dataservice),
    ]);
  }

  // Flush queued mutations the moment connectivity returns, instead of waiting
  // for the next 10-min interval tick. Reuses the bootstrap store's existing
  // online/offline signal (`isOffline`, driven by authStore.watchFirestoreOffline)
  // — no new connectivity check is introduced.
  function watchOnlineReconnect() {
    if (!process.client || reconnectWatchStop) return;
    const bootstrap = useBootstrapStore();
    reconnectWatchStop = watch(
      () => bootstrap.isOffline,
      (offline, wasOffline) => {
        // Offline → Online transition only.
        if (wasOffline === true && offline === false) {
          syncAllOnReconnect().catch((e) =>
            console.error("[receipts reconnect-sync] failed:", e)
          );
        }
      }
    );
  }

  async function bootOnline() {
    if (isInitialized.value) return;
    isInitialized.value = true;

    // Online: skip restore, just seed knownKeys so cloud-fetched receipts
    // already cached don't rewrite IDB.
    const { knownKeys: seeded } = await loadPerRowBucket<ReceiptOrderDynamodb>(getReceiptsBucket(), false);
    seeded.forEach((k) => knownKeys.add(k));

    receiptIdCounter.boot();

    const businessDate = todaysBusinessDate();
    // Terminal / readonly devices can't view totals, so skip the closed-bill
    // "By Receive" fetch entirely. Still mark receipts loaded so `loaded`
    // resolves and the local/IDB merge below runs normally.
    if (usePosShopStore().isRestrictedMode) {
      subLoaded.value.receipts = true;
    } else {
      await fetch(businessDate, businessDate);
    }
    refreshPendingReceipts();
    refreshVoidCommands();
    mergePendingReceiptsIntoState();
    // Replay any counter increments left queued from a previous session that
    // closed before syncing, so the cloud counter catches up to the local id.
    void receiptIdCounter.flushCounterIncrements();
    // Series sync: pending receipts first, then replay queued voids.
    flushOfflineMutations();

    queryReceipts.value.startDate = businessDate;
    queryReceipts.value.endDate = businessDate;
    startAutoSyncInterval();
    watchOnlineReconnect();
  }

  async function bootOffline() {
    if (isInitialized.value) return;
    isInitialized.value = true;

    const { entries, knownKeys: seeded } = await loadPerRowBucket<ReceiptOrderDynamodb>(getReceiptsBucket(), true);
    seeded.forEach((k) => knownKeys.add(k));
    if (entries.length) {
      receipts.value = _.orderBy(
        entries.map(([, v]) => v),
        ["Date", "OrderDate", "createdAt"],
        ["desc", "desc", "desc"]
      );
    }
    subLoaded.value.receipts = true;

    // Counter boot restores from localStorage AND marks counter loaded
    // synchronously, then queues cloud listeners that take over on reconnect.
    receiptIdCounter.boot();

    refreshPendingReceipts();
    refreshVoidCommands();
    mergePendingReceiptsIntoState();

    const businessDate = todaysBusinessDate();
    queryReceipts.value.startDate = businessDate;
    queryReceipts.value.endDate = businessDate;
    startAutoSyncInterval();
    watchOnlineReconnect();
  }

  const init = async () => {
    if (isOfflineBoot()) return bootOffline();
    return bootOnline();
  };

  return {
    name: "receipts",
    loaded,
    queryReceipts,

    receipts,
    data,
    init,
    bootOnline,
    bootOffline,
    refresh,
    fetch,
    refreshFromServer,
    setReceipts,
    add,
    post,
    update,
    remove,
    ensureSynced,
    getPendingReceipts,
    mergePendingReceiptsIntoState,
    syncPendingReceipts,
    flushOfflineMutations,
    markReceiptSynced,
    pendingReceipts,
    pendingNonces,
    refreshPendingReceipts,
    isReceiptUnsynced,
    unsyncedCount,
    delayedUnsyncedCount,
    // offline void queue
    voidCommands,
    pendingVoidNonces,
    isVoidPending,
    refreshVoidCommands,
  };
});
